import React from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import FormatSizeRoundedIcon from "@mui/icons-material/FormatSizeRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EditableImage from "../landingEditor/EditableImage";
import { getLandingSegments, landingSegmentAnchor } from "../landingEditor/landingLayout";
import {
  DEFAULT_SEGMENT_VISUAL_STYLE,
  getSegmentVisualStyle,
  updateSegmentVisualStyle,
} from "./segmentVisualStyle";
import {
  buildSegmentVisibilityChanges,
  getSegmentDefinition,
  SEGMENT_FIELD_TYPES,
} from "./segmentRegistry";
import {
  TAB_INDEX,
  INDEX_TAB,
  CONTENT_SECTION_BY_TOOL,
  DESIGN_SECTION_BY_TOOL,
  FONT_PRESETS,
  PALETTE_THEME_PATHS,
  SECTION_COLOR_TOKENS,
  normalizeEditableColor,
  isTransparentColor,
  globalSectionPalette,
  paletteFromForm,
  siteColorChanges,
  paletteChanges,
  randomizedReadablePalette,
  EMPTY_SEGMENT_PROPERTIES,
  getAtPath,
  editableLayoutSegment,
  customSectionDefinition,
  withSectionBackgroundProperties,
  BEHAVIOR_FIELD_TYPES,
  DIRECT_CONTENT_FIELD_TYPES,
  COLLECTION_CONTENT_FIELD_TYPES,
  HIGHLIGHT_ICON_COMPONENTS,
  collectionTextFields,
  elementTextColorEligible,
  elementTextFallback,
  registeredFieldValue,
  sectionColorSeed,
  registryFieldError,
  segmentAvailableAsDestination,
  navbarPropertyAvailable,
  levelOptionsFromRegistry,
  normalizeVisualPatch,
  toColorInput,
  imageFieldsFromRegistry,
  styleFieldsFromRegistry,
} from "./inspector/inspectorModel";
import LandingCopyTextField from "./inspector/LandingCopyTextField";
import { VisualSliderControl } from "./inspector/VisualSettingControls";
import {
  ButtonDestinationEditor,
  RegisteredBehaviorTextField,
  NavbarButtonsEditor,
  IndividualTextColorControl,
  LevelSelector,
  InspectorAccordion,
} from "./inspector/LandingInspectorControls";

function SectionColorCopyButton({ label, value, disabled = false }) {
  const [feedback, setFeedback] = React.useState("");
  const feedbackTimerRef = React.useRef(null);

  const clearFeedbackTimer = React.useCallback(() => {
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => () => clearFeedbackTimer(), [clearFeedbackTimer]);

  const showFeedback = React.useCallback((status) => {
    clearFeedbackTimer();
    setFeedback(status);
    feedbackTimerRef.current = setTimeout(() => {
      setFeedback("");
      feedbackTimerRef.current = null;
    }, 1800);
  }, [clearFeedbackTimer]);

  const copyValue = React.useCallback(async () => {
    if (disabled) return;
    try {
      if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(value);
      showFeedback("success");
    } catch {
      showFeedback("error");
    }
  }, [disabled, showFeedback, value]);

  const feedbackText = feedback === "success"
    ? "Copiado"
    : feedback === "error"
      ? "No se pudo copiar"
      : `Copiar ${value}`;

  return (
    <Box sx={{ position: "relative" }}>
      {feedback ? (
        <Box
          component="span"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: "absolute",
            right: -4,
            bottom: "calc(100% + 6px)",
            zIndex: 2,
            px: 0.8,
            py: 0.35,
            borderRadius: 1.25,
            bgcolor: feedback === "error" ? "error.main" : "success.main",
            color: "common.white",
            fontSize: "0.68rem",
            fontWeight: 850,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            boxShadow: 1,
            pointerEvents: "none",
          }}
        >
          {feedbackText}
        </Box>
      ) : null}
      <Tooltip title={feedbackText}>
        <span>
          <IconButton
            size="small"
            edge="end"
            disabled={disabled}
            aria-label={`Copiar ${label}: ${value}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={copyValue}
          >
            <ContentCopyRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default function LandingSegmentInspector({
  open,
  sectionId,
  initialTool = "content",
  initialImagePath = "",
  form,
  baselineForm,
  productsEnabled,
  saving,
  onClose,
  onCommit,
  onCommitMany,
  onUploadImage,
  onRemoveImage,
}) {
  const mobile = useMediaQuery("(max-width:760px)");
  const compactEditor = useMediaQuery("(max-width:1100px)");
  const [tab, setTab] = React.useState(TAB_INDEX[initialTool] ?? 0);
  const [expandedContentSection, setExpandedContentSection] = React.useState(
    CONTENT_SECTION_BY_TOOL[initialTool] || false
  );
  const [expandedDesignSection, setExpandedDesignSection] = React.useState(
    DESIGN_SECTION_BY_TOOL[initialTool] || false
  );
  const [colorDraft, setColorDraft] = React.useState({});
  const [paletteDraft, setPaletteDraft] = React.useState(() => paletteFromForm(form));
  const [sectionPaletteDraft, setSectionPaletteDraft] = React.useState(() => globalSectionPalette(form));
  const activeLayout = React.useMemo(
    () => editableLayoutSegment(form, sectionId),
    [form, sectionId]
  );
  const customInstance = activeLayout.segment?.type === "custom" ? activeLayout.segment : null;
  const definition = React.useMemo(() => {
    const baseDefinition = customInstance
      ? customSectionDefinition(customInstance, activeLayout.index)
      : getSegmentDefinition(sectionId);
    return withSectionBackgroundProperties(baseDefinition, activeLayout.segment, activeLayout.index);
  }, [activeLayout.index, activeLayout.segment, customInstance, sectionId]);
  const registeredProperties = React.useMemo(() => {
    const properties = definition?.properties || EMPTY_SEGMENT_PROPERTIES;
    if (sectionId !== "navbar") return properties;
    return properties.filter((property) => navbarPropertyAvailable(form, property, productsEnabled));
  }, [definition, form, productsEnabled, sectionId]);
  const label = definition?.label || "Sección";
  const visualField = registeredProperties.find(
    (property) => property.type === SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE
  );
  const storedVisualStyle = activeLayout.segment?.visualStyle || {};
  const visual = customInstance
    ? { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...(customInstance.visualStyle || {}) }
    : getSegmentVisualStyle(form?.theme, sectionId);
  const useGlobalSectionColors = storedVisualStyle.useGlobalColors !== false;
  const canUseSectionPalette = activeLayout.index >= 0 && sectionId !== "floatingWhatsapp";
  const individualElementColorsEnabled = canUseSectionPalette;
  const currentElementColors = storedVisualStyle.elementColors && typeof storedVisualStyle.elementColors === "object"
    ? storedVisualStyle.elementColors
    : {};
  const individualTextColor = React.useCallback(
    (path) => normalizeEditableColor(currentElementColors?.[path]?.color || ""),
    [currentElementColors]
  );
  const individualTextFallback = React.useCallback(
    (path) => elementTextFallback(path, { ...globalSectionPalette(form), ...(storedVisualStyle.colors || {}) }, sectionId),
    [form, sectionId, storedVisualStyle.colors]
  );
  const colorFields = React.useMemo(
    () => registeredProperties.filter(
      (property) => property.editable !== false && property.type === SEGMENT_FIELD_TYPES.COLOR
    ),
    [registeredProperties]
  );
  const siteColorFields = React.useMemo(
    () => colorFields.filter((field) =>
      field.colorScope === "site" || String(field.path || "").startsWith("theme.")
    ),
    [colorFields]
  );
  const sectionColorFields = React.useMemo(
    () => colorFields.filter((field) => !siteColorFields.includes(field)),
    [colorFields, siteColorFields]
  );
  const hasFullSitePalette = React.useMemo(
    () => PALETTE_THEME_PATHS.every((path) => siteColorFields.some((field) => field.path === path)),
    [siteColorFields]
  );
  const extraSiteColorFields = React.useMemo(
    () => siteColorFields.filter((field) => !hasFullSitePalette || !PALETTE_THEME_PATHS.includes(field.path)),
    [hasFullSitePalette, siteColorFields]
  );
  const imageFields = React.useMemo(
    () => imageFieldsFromRegistry(definition, form),
    [definition, form]
  );
  const imageCardRefs = React.useRef({});
  const styleFields = React.useMemo(
    () => styleFieldsFromRegistry(definition, imageFields),
    [definition, imageFields]
  );
  const designControlFields = React.useMemo(
    () => registeredProperties.filter((property) =>
      property.editable !== false &&
      property.uiGroup === "design" &&
      !styleFields.some((styleField) => styleField.path === property.path)
    ),
    [registeredProperties, styleFields]
  );
  const cardVisibilityFields = React.useMemo(
    () => sectionId === "featured"
      ? registeredProperties.filter((property) =>
          property.editable !== false &&
          property.type === SEGMENT_FIELD_TYPES.BOOLEAN &&
          /^catalog\.cards\.show(?:Image|Title|Button|Type|Category|Subcategory|Description|Price|Duration|Capabilities)$/.test(property.path || "")
        )
      : [],
    [registeredProperties, sectionId]
  );
  const cardVisibilityPaths = React.useMemo(
    () => new Set(cardVisibilityFields.map((property) => property.path)),
    [cardVisibilityFields]
  );
  const visibilityPath = definition?.visibilityPath;
  const behaviorFields = React.useMemo(
    () => registeredProperties.filter((property) =>
      property.editable !== false &&
      property.path !== visibilityPath &&
      !cardVisibilityPaths.has(property.path) &&
      (property.uiGroup === "behavior" || BEHAVIOR_FIELD_TYPES.has(property.type))
    ),
    [cardVisibilityPaths, registeredProperties, visibilityPath]
  );
  const internalAnchorOptions = React.useMemo(() =>
    getLandingSegments(form)
      .filter((segment) => segmentAvailableAsDestination(form, segment, productsEnabled))
      .map((segment) => ({
        value: landingSegmentAnchor(segment),
        label: segment.type === "custom"
          ? (segment.data?.custom?.title || segment.anchor || segment.id || "Sección libre")
          : (getSegmentDefinition(segment.type)?.label || segment.type),
      }))
      .filter((option) => option.value),
    [form, productsEnabled]
  );
  const contentFields = React.useMemo(
    () => registeredProperties.filter((property) =>
      property.editable !== false &&
      property.path !== "hero.overlay" &&
      DIRECT_CONTENT_FIELD_TYPES.has(property.type)
    ),
    [registeredProperties]
  );
  const contentCollections = React.useMemo(
    () => registeredProperties
      .filter((property) =>
        property.editable !== false &&
        property.type === SEGMENT_FIELD_TYPES.COLLECTION &&
        collectionTextFields(property).length > 0
      ),
    [registeredProperties]
  );
  const visibilityField = registeredProperties.find(
    (property) => property.path === visibilityPath && property.type === SEGMENT_FIELD_TYPES.BOOLEAN
  );
  const overlayField = registeredProperties.find((property) => property.path === "hero.overlay");
  const moduleUnavailable = Boolean(definition?.requiresProducts && !productsEnabled);
  const visible = visibilityPath
    ? getAtPath(form, visibilityPath, definition?.defaultVisible ?? true) !== false
    : true;
  const paletteValidationField = React.useMemo(() => ({
    type: SEGMENT_FIELD_TYPES.COLOR,
    maxLength: Math.max(18, ...siteColorFields.map((field) => field.maxLength || 0)),
  }), [siteColorFields]);
  const colorErrors = React.useMemo(
    () => Object.fromEntries(
      colorFields.map((field) => [field.path, registryFieldError(field, colorDraft[field.path])])
    ),
    [colorDraft, colorFields]
  );
  const paletteErrors = React.useMemo(
    () => hasFullSitePalette
      ? paletteDraft.map((value) => registryFieldError(paletteValidationField, value))
      : [],
    [hasFullSitePalette, paletteDraft, paletteValidationField]
  );
  const hasColorErrors = Object.values(colorErrors).some(Boolean) || paletteErrors.some(Boolean);
  const spacingOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "spacing", {
      1: "Compacto",
      2: "Equilibrado",
      3: "Amplio",
    }),
    [visualField]
  );
  const titleSizeOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "titleSize", {
      1: "Pequeño",
      2: "Normal",
      3: "Grande",
    }),
    [visualField]
  );
  const textStyleOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "textStyle", {
      1: "Suave",
      2: "Firme",
      3: "Impactante",
    }),
    [visualField]
  );
  const imageSizeOptions = React.useMemo(
    () => levelOptionsFromRegistry(visualField, "imageSize", {
      1: "Pequeña",
      2: "Normal",
      3: "Grande",
    }),
    [visualField]
  );

  React.useEffect(() => {
    if (!open) return;
    setTab(TAB_INDEX[initialTool] ?? 0);
    setExpandedContentSection(CONTENT_SECTION_BY_TOOL[initialTool] || false);
    setExpandedDesignSection(DESIGN_SECTION_BY_TOOL[initialTool] || false);
  }, [initialTool, open, sectionId]);

  React.useEffect(() => {
    if (!open || initialTool !== "image" || !initialImagePath) return undefined;
    const timer = window.setTimeout(() => {
      imageCardRefs.current[initialImagePath]?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [initialImagePath, initialTool, open, sectionId, imageFields.length]);

  React.useEffect(() => {
    const next = {};
    colorFields.forEach((field) => {
      const isSiteColor = field.colorScope === "site" || String(field.path || "").startsWith("theme.");
      if (!isSiteColor) {
        next[field.path] = getAtPath(form, field.path, "") || "";
        return;
      }
      next[field.path] = registeredFieldValue(form, field, field.defaultValue || "#000000");
    });
    setColorDraft(next);
    setPaletteDraft(paletteFromForm(form));
    const globalPalette = globalSectionPalette(form);
    setSectionPaletteDraft({ ...globalPalette, ...(activeLayout.segment?.visualStyle?.colors || {}) });
  }, [colorFields, form, sectionId, activeLayout.segment]);

  const updateVisual = (patch) => {
    if (!visualField) return;
    const normalizedPatch = normalizeVisualPatch(visualField, patch);
    if (customInstance && activeLayout.index >= 0) {
      onCommit(
        `layout.segments.${activeLayout.index}.visualStyle`,
        { ...DEFAULT_SEGMENT_VISUAL_STYLE, ...(customInstance.visualStyle || {}), ...normalizedPatch }
      );
      return;
    }
    const template = updateSegmentVisualStyle(form?.theme?.template, sectionId, normalizedPatch);
    onCommit("theme.template", template);
  };

  const commitIndividualTextColor = (path, nextColor) => {
    if (!canUseSectionPalette || !elementTextColorEligible(path)) return;
    const current = activeLayout.segment?.visualStyle || {};
    const nextElementColors = { ...(current.elementColors || {}) };
    const previousStyle = nextElementColors[path] && typeof nextElementColors[path] === "object"
      ? { ...nextElementColors[path] }
      : {};
    const normalized = normalizeEditableColor(nextColor);
    if (normalized) previousStyle.color = normalized;
    else delete previousStyle.color;
    if (Object.keys(previousStyle).length) nextElementColors[path] = previousStyle;
    else delete nextElementColors[path];

    const nextVisualStyle = { ...current };
    if (Object.keys(nextElementColors).length) nextVisualStyle.elementColors = nextElementColors;
    else delete nextVisualStyle.elementColors;
    onCommit(`layout.segments.${activeLayout.index}.visualStyle`, nextVisualStyle);
  };

  const resetIndividualTextColor = (path) => commitIndividualTextColor(path, "");

  const commitSectionColorMode = (useGlobalColors) => {
    if (!canUseSectionPalette) return;
    const current = activeLayout.segment?.visualStyle || {};
    const globalPalette = globalSectionPalette(form);
    const nextColors = useGlobalColors
      ? (current.colors || sectionPaletteDraft)
      : { ...globalPalette, ...(current.colors || sectionPaletteDraft) };
    setSectionPaletteDraft(nextColors);
    const visualPath = `layout.segments.${activeLayout.index}.visualStyle`;
    if (useGlobalColors && ["about", "highlights", "contact"].includes(sectionId)) {
      onCommitMany({
        [visualPath]: { ...current, useGlobalColors: true, colors: nextColors },
        [`appearance.${sectionId}.backgroundColor`]: "",
        [`appearance.${sectionId}.surfaceColor`]: "",
        [`appearance.${sectionId}.accentColor`]: "",
        [`appearance.${sectionId}.titleColor`]: "",
        [`appearance.${sectionId}.textColor`]: "",
      });
      return;
    }
    onCommit(visualPath, {
      ...current,
      useGlobalColors,
      colors: nextColors,
    });
  };

  const applySectionPalette = () => {
    if (!canUseSectionPalette || useGlobalSectionColors) return;
    const current = activeLayout.segment?.visualStyle || {};
    onCommit(`layout.segments.${activeLayout.index}.visualStyle`, {
      ...current,
      useGlobalColors: false,
      colors: Object.fromEntries(
        SECTION_COLOR_TOKENS.map(([key]) => [key, normalizeEditableColor(sectionPaletteDraft[key])])
      ),
    });
  };

  const setPaletteColor = (index, value) => {
    const path = PALETTE_THEME_PATHS[index];
    setPaletteDraft((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
    if (path) {
      setColorDraft((current) => ({ ...current, [path]: value }));
    }
  };

  const setSectionColorOverride = (field, enabled) => {
    setColorDraft((current) => ({
      ...current,
      [field.path]: enabled
        ? current[field.path] || sectionColorSeed(form, field) || "#000000"
        : "",
    }));
  };

  const applyColors = () => {
    if (hasColorErrors) return;
    const changes = {};
    siteColorFields.forEach((field) => {
      Object.assign(changes, siteColorChanges(field.path, colorDraft[field.path] || field.defaultValue || ""));
    });
    sectionColorFields.forEach((field) => {
      changes[field.path] = colorDraft[field.path] || "";
    });
    if (Object.keys(changes).length) onCommitMany(changes);
  };

  const randomizeColors = () => {
    if (!hasFullSitePalette || paletteErrors.some(Boolean)) return;
    const randomized = randomizedReadablePalette(paletteDraft);
    const changes = paletteChanges(randomized);
    setPaletteDraft(randomized);
    setColorDraft((current) => ({
      ...current,
      ...Object.fromEntries(PALETTE_THEME_PATHS.map((path, index) => [path, randomized[index]])),
    }));
    onCommitMany(changes);
  };

  const resetSectionColors = () => {
    const changes = Object.fromEntries(sectionColorFields.map((field) => [field.path, ""]));
    if (!Object.keys(changes).length) return;
    setColorDraft((current) => ({ ...current, ...changes }));
    onCommitMany(changes);
  };

  const setVisible = (next) => {
    if (!visibilityPath || moduleUnavailable) return;
    if (customInstance && activeLayout.index >= 0) {
      onCommit(visibilityPath, next === true);
      return;
    }
    const changes = buildSegmentVisibilityChanges(sectionId, next);
    const entries = Object.entries(changes);
    if (!entries.length) return;
    if (entries.length === 1) onCommit(entries[0][0], entries[0][1]);
    else onCommitMany(changes);
  };

  const moveCollectionImage = (field, direction) => {
    if (!field?.collectionPath || !Number.isInteger(field.collectionIndex)) return;
    const items = getAtPath(form, field.collectionPath, []);
    if (!Array.isArray(items) || items.length < 2) return;

    const from = field.collectionIndex;
    const to = from + direction;
    const visibleLength = Number(field.collectionLength || items.length);
    if (from < 0 || from >= items.length || to < 0 || to >= items.length) return;
    if (to >= visibleLength) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onCommit(field.collectionPath, next);
  };

  // Las secciones migradas tienen un único flujo contextual. Este inspector queda
  // exclusivamente como fallback para definiciones todavía no migradas.
  if (definition?.elementEditingReady === true) return null;

  const drawerPaper = compactEditor
    ? {
        width: "100%",
        maxWidth: "100%",
        maxHeight: mobile ? "88vh" : "74vh",
        borderRadius: "20px 20px 0 0",
      }
    : { width: 410, maxWidth: "92vw" };

  return (
    <Drawer
      anchor={compactEditor ? "bottom" : "right"}
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          ...drawerPaper,
          ...(compactEditor ? {
            "& .MuiButton-root": { minHeight: 44 },
            "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 },
            "& .MuiInputBase-root": { minHeight: 44 },
            "& .MuiFormControlLabel-root": { minHeight: 44 },
            "& input[type='color']": { minWidth: 48, minHeight: 44 },
          } : {}),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: compactEditor ? (mobile ? "88vh" : "74vh") : "100%",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 2, pt: 1.7, pb: 1.1 }}>
          <Box>
            <Typography variant="overline" sx={{ color: "#F35A3A", fontWeight: 950, letterSpacing: 1 }}>
              EDITAR SEGMENTO
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F", lineHeight: 1.1 }}>
              {label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={saving} aria-label="Cerrar opciones">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="fullWidth"
          sx={{
            px: 1,
            minHeight: 46,
            borderBottom: "1px solid rgba(25,36,47,.09)",
            "& .MuiTab-root": {
              minHeight: 46,
              minWidth: 0,
              px: 1,
              textTransform: "none",
              fontWeight: 900,
            },
          }}
        >
          <Tab icon={<TextFieldsRoundedIcon />} iconPosition="start" label="Contenido" />
          <Tab icon={<PaletteRoundedIcon />} iconPosition="start" label="Diseño" />
          <Tab icon={<VisibilityRoundedIcon />} iconPosition="start" label="Comportamiento" />
        </Tabs>

        <Box sx={{ p: 2, overflowY: "auto", flex: 1, bgcolor: "#F7F8FA" }}>
          {INDEX_TAB[tab] === "content" ? (
            <Stack spacing={1.5}>
              {!definition ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento todavía no tiene una definición registrada.
                </Alert>
              ) : null}
              {moduleUnavailable ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento requiere el módulo de productos o servicios para poder editar su contenido visible.
                </Alert>
              ) : null}

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(25,36,47,.09)",
                }}
              >
                <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                  Textos y contenido
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.62)" }}>
                  Editá títulos, descripciones, etiquetas de botones y colecciones sin mezclar enlaces ni opciones de visibilidad.
                </Typography>
                {contentFields.length || contentCollections.length ? (
                  <Stack spacing={1.25} sx={{ mt: 1.4 }}>
                    {contentFields.map((field) => {
                      const value = registeredFieldValue(form, field, "");
                      const showTextColor = individualElementColorsEnabled && elementTextColorEligible(field.path, field);
                      return (
                        <Box key={`${field.path}:${String(value)}`}>
                          <LandingCopyTextField
                            fullWidth
                            size="small"
                            type={field.type === SEGMENT_FIELD_TYPES.EMAIL ? "email" : "text"}
                            label={field.label}
                            field={field}
                            path={field.path}
                            form={form}
                            baselineForm={baselineForm}
                            value={value ?? ""}
                            multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA}
                            minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined}
                            disabled={saving || moduleUnavailable}
                            helperText="Opcional. Si lo dejás vacío, ese texto no se muestra."
                            onCommit={(nextValue) => onCommit(field.path, nextValue)}
                          />
                          {showTextColor ? (
                            <IndividualTextColorControl
                              fieldPath={field.path}
                              label={field.label || "Texto"}
                              value={individualTextColor(field.path)}
                              fallback={individualTextFallback(field.path)}
                              disabled={saving || moduleUnavailable}
                              onChange={(next) => commitIndividualTextColor(field.path, next)}
                              onReset={() => resetIndividualTextColor(field.path)}
                            />
                          ) : null}
                        </Box>
                      );
                    })}

                    {contentCollections.map((property) => {
                      const items = registeredFieldValue(form, property, []);
                      const safeItems = Array.isArray(items) ? items : [];
                      const fields = collectionTextFields(property);
                      const maxItems = Number.isFinite(property.limits?.maxItems)
                        ? property.limits.maxItems
                        : safeItems.length;
                      const visibleItems = safeItems.slice(0, maxItems);
                      const shouldScrollCollection = maxItems >= 30 && visibleItems.length > 8;

                      if (property.navbarButtons === true) {
                        return (
                          <NavbarButtonsEditor
                            key={property.path}
                            property={property}
                            items={safeItems}
                            form={form}
                            baselineForm={baselineForm}
                            options={internalAnchorOptions}
                            disabled={saving || moduleUnavailable}
                            onCommit={onCommit}
                            showIndividualColors={individualElementColorsEnabled}
                            getElementColor={individualTextColor}
                            getElementFallback={individualTextFallback}
                            onElementColorChange={commitIndividualTextColor}
                            onElementColorReset={resetIndividualTextColor}
                          />
                        );
                      }

                      const addCollectionItem = () => {
                        if (safeItems.length >= maxItems) return;
                        const primitive = property.item?.type && COLLECTION_CONTENT_FIELD_TYPES.has(property.item.type);
                        if (primitive) {
                          onCommit(property.path, [...safeItems, ""]);
                          return;
                        }
                        const nextItem = {};
                        fields.forEach((itemField) => {
                          if (!itemField.key) return;
                          if (itemField.key === "id") nextItem[itemField.key] = `item-${Date.now().toString(36)}`;
                          else if (itemField.key === "imagePosition") nextItem[itemField.key] = "center";
                          else nextItem[itemField.key] = "";
                        });
                        onCommit(property.path, [...safeItems, nextItem]);
                      };
                      const removeCollectionItem = (index) => {
                        onCommit(property.path, safeItems.filter((_, itemIndex) => itemIndex !== index));
                      };

                      return (
                        <Box
                          key={property.path}
                          sx={{
                            p: 1.25,
                            borderRadius: 2.5,
                            bgcolor: "#F7F8FA",
                            border: "1px solid rgba(25,36,47,.08)",
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography sx={{ color: "#19242F", fontWeight: 900 }}>
                              {property.label}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={saving || moduleUnavailable || safeItems.length >= maxItems}
                              onClick={addCollectionItem}
                              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                            >
                              Agregar
                            </Button>
                          </Stack>
                          {!visibleItems.length ? (
                            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)" }}>
                              No hay elementos. Podés agregar el primero cuando quieras.
                            </Typography>
                          ) : null}
                          <Stack
                            spacing={1.15}
                            sx={shouldScrollCollection ? { maxHeight: 520, overflowY: "auto", pr: 0.5 } : undefined}
                          >
                            {visibleItems.map((item, index) => (
                              <Box key={`${property.path}:${index}`} sx={{ p: 1.1, borderRadius: 2.25, bgcolor: "#fff" }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.85 }}>
                                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.56)", fontWeight: 850 }}>
                                    {property.label} {index + 1}
                                  </Typography>
                                  <Button
                                    size="small"
                                    color="error"
                                    disabled={saving || moduleUnavailable}
                                    onClick={() => removeCollectionItem(index)}
                                    sx={{ minWidth: 0, px: 1, textTransform: "none", fontWeight: 800 }}
                                  >
                                    Quitar
                                  </Button>
                                </Stack>
                                <Stack spacing={1}>
                                  {fields.map((itemField) => {
                                    const primitive = itemField.key === "";
                                    const value = primitive ? item ?? "" : item?.[itemField.key] ?? "";
                                    if (itemField.type === SEGMENT_FIELD_TYPES.SELECT) {
                                      return (
                                        <FormControl
                                          key={`${property.path}:${index}:${itemField.key || "value"}:${String(value)}`}
                                          fullWidth
                                          size="small"
                                        >
                                          <InputLabel>{itemField.label || "Ícono MUI"}</InputLabel>
                                          <Select
                                            label={itemField.label || "Ícono MUI"}
                                            value={value}
                                            disabled={saving || moduleUnavailable}
                                            onChange={(event) => {
                                              const nextItems = [...safeItems];
                                              nextItems[index] = {
                                                ...(nextItems[index] || {}),
                                                [itemField.key]: event.target.value,
                                              };
                                              onCommit(property.path, nextItems);
                                            }}
                                          >
                                            {(itemField.options || []).map((option) => {
                                              const Icon = HIGHLIGHT_ICON_COMPONENTS[option.value];
                                              return (
                                                <MenuItem key={option.value || "none"} value={option.value}>
                                                  <Stack direction="row" spacing={1} alignItems="center">
                                                    {Icon ? <Icon sx={{ fontSize: 19 }} /> : <Box sx={{ width: 19, height: 19 }} />}
                                                    <span>{option.label}</span>
                                                  </Stack>
                                                </MenuItem>
                                              );
                                            })}
                                          </Select>
                                          <FormHelperText>Opcional. Elegí “Sin ícono” para no mostrar ninguno.</FormHelperText>
                                        </FormControl>
                                      );
                                    }

                                    const elementPath = primitive
                                      ? `${property.path}.${index}`
                                      : `${property.path}.${index}.${itemField.key}`;
                                    const showTextColor = individualElementColorsEnabled && elementTextColorEligible(elementPath, itemField);
                                    return (
                                      <Box key={`${property.path}:${index}:${itemField.key || "value"}:${String(value)}`}>
                                        <LandingCopyTextField
                                          fullWidth
                                          size="small"
                                          label={primitive ? property.label : itemField.label}
                                          field={itemField}
                                          path={elementPath}
                                          form={form}
                                          baselineForm={baselineForm}
                                          value={value}
                                          multiline={itemField.type === SEGMENT_FIELD_TYPES.TEXTAREA}
                                          minRows={itemField.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 2 : undefined}
                                          disabled={saving || moduleUnavailable}
                                          helperText="Opcional"
                                          onCommit={(nextValue) => {
                                            if (nextValue === value) return;
                                            const nextItems = [...safeItems];
                                            nextItems[index] = primitive
                                              ? nextValue
                                              : { ...(nextItems[index] || {}), [itemField.key]: nextValue };
                                            onCommit(property.path, nextItems);
                                          }}
                                        />
                                        {showTextColor ? (
                                          <IndividualTextColorControl
                                            fieldPath={elementPath}
                                            label={primitive ? `${property.label} ${index + 1}` : `${itemField.label} ${index + 1}`}
                                            value={individualTextColor(elementPath)}
                                            fallback={individualTextFallback(elementPath)}
                                            disabled={saving || moduleUnavailable}
                                            onChange={(next) => commitIndividualTextColor(elementPath, next)}
                                            onReset={() => resetIndividualTextColor(elementPath)}
                                          />
                                        ) : null}
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ mt: 1.2, color: "rgba(25,36,47,.58)" }}>
                    Esta sección no tiene textos editables. Usá Imágenes, Diseño o Comportamiento según corresponda.
                  </Typography>
                )}
              </Box>

              {imageFields.length ? (
                <InspectorAccordion
                  id="image-content"
                  expanded={expandedContentSection}
                  onChange={setExpandedContentSection}
                  icon={ImageRoundedIcon}
                  title="Imágenes"
                  description="Elegí, reemplazá, eliminá y ordená imágenes cuando la sección lo permita"
                >
                  <Stack
                    spacing={1.4}
                    sx={imageFields.length > 8 ? { maxHeight: 620, overflowY: "auto", pr: 0.5 } : undefined}
                  >
                    {imageFields.map((field) => {
                      const canReorder = Boolean(
                        field.collectionPath && Number(field.collectionLength || 0) > 1
                      );
                      const first = field.collectionIndex === 0;
                      const last = field.collectionIndex >= Number(field.collectionLength || 0) - 1;

                      return (
                        <Box
                          key={field.path}
                          ref={(node) => {
                            if (node) imageCardRefs.current[field.path] = node;
                            else delete imageCardRefs.current[field.path];
                          }}
                          sx={{
                            p: 1.2,
                            borderRadius: 3,
                            bgcolor: "#fff",
                            border: field.path === initialImagePath
                              ? "2px solid rgba(243,90,58,.72)"
                              : "1px solid rgba(25,36,47,.09)",
                            boxShadow: field.path === initialImagePath
                              ? "0 0 0 3px rgba(243,90,58,.08)"
                              : "none",
                          }}
                        >
                          <EditableImage
                            value={getAtPath(form, field.path, "")}
                            label={field.label}
                            helperText={field.positionPath
                              ? "El encuadre y el tamaño visual se ajustan desde Diseño."
                              : "El tamaño visual se ajusta desde Diseño."}
                            height={150}
                            disabled={saving || moduleUnavailable}
                            onUpload={(file, options) => onUploadImage(field.path, file, { sectionKey: sectionId, assetKey: field.key }, options)}
                            onRemove={() => onRemoveImage(field.path)}
                          />

                          {visualField || field.positionPath ? (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<TuneRoundedIcon />}
                              disabled={saving || moduleUnavailable}
                              onClick={() => {
                                setTab(1);
                                setExpandedDesignSection("image-design");
                              }}
                              sx={{ mt: 0.8, borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                            >
                              {field.positionPath ? "Ajustar tamaño y encuadre" : "Ajustar tamaño visual"}
                            </Button>
                          ) : null}

                          {canReorder ? (
                            <Box sx={{ mt: 1.25, pt: 1.15, borderTop: "1px solid rgba(25,36,47,.08)" }}>
                              <Typography variant="caption" sx={{ display: "block", mb: 0.8, color: "rgba(25,36,47,.6)", fontWeight: 850 }}>
                                Orden en {String(field.collectionLabel || "la sección").toLowerCase()} · posición {field.collectionIndex + 1} de {field.collectionLength}
                              </Typography>
                              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<KeyboardArrowUpRoundedIcon />}
                                  disabled={saving || moduleUnavailable || first}
                                  onClick={() => moveCollectionImage(field, -1)}
                                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                                >
                                  Mover arriba
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<KeyboardArrowDownRoundedIcon />}
                                  disabled={saving || moduleUnavailable || last}
                                  onClick={() => moveCollectionImage(field, 1)}
                                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                                >
                                  Mover abajo
                                </Button>
                              </Stack>
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                </InspectorAccordion>
              ) : null}

              {cardVisibilityFields.length ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    border: "1px solid rgba(25,36,47,.09)",
                  }}
                >
                  <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                    Contenido visible de las cards
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.35, mb: 1.2, color: "rgba(25,36,47,.62)" }}>
                    Elegí qué datos aparecen en cada producto o servicio destacado. Se usan los mismos campos que guarda el backend.
                  </Typography>
                  <Stack spacing={0.45}>
                    {cardVisibilityFields.map((field) => {
                      const value = registeredFieldValue(form, field, field.defaultValue ?? true);
                      return (
                        <FormControlLabel
                          key={field.path}
                          control={(
                            <Switch
                              checked={value !== false}
                              disabled={saving || moduleUnavailable}
                              onChange={(event) => onCommit(field.path, event.target.checked)}
                            />
                          )}
                          label={field.label}
                          sx={{ m: 0 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}

              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Colores, tipografías, espaciado y encuadre están en Diseño. Enlaces y otras reglas están en Comportamiento.
              </Alert>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "design" ? (
            <Stack spacing={1.35}>
              <Box
                sx={{
                  p: 1.45,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(25,36,47,.09)",
                }}
              >
                <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                  Ajustes frecuentes
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.3, mb: 1.45, color: "rgba(25,36,47,.62)" }}>
                  Empezá por la distribución y el aire de la sección. El resto queda agrupado debajo.
                </Typography>

                <Stack spacing={1.65}>
                  {visualField ? (
                    <LevelSelector
                      label="Aire y separación del segmento"
                      value={visual.spacing}
                      options={spacingOptions}
                      onChange={(spacing) => updateVisual({ spacing })}
                    />
                  ) : null}

                  {styleFields.map((field) => {
                    const value = registeredFieldValue(form, field, field.defaultValue || "");
                    const error = registryFieldError(field, value);
                    return (
                      <FormControl key={field.path} fullWidth size="small" error={Boolean(error)}>
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                          label={field.label}
                          value={value}
                          disabled={saving}
                          onChange={(event) => {
                            if (!registryFieldError(field, event.target.value)) {
                              onCommit(field.path, event.target.value);
                            }
                          }}
                        >
                          {(field.options || []).map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error ? <FormHelperText>{error}</FormHelperText> : null}
                      </FormControl>
                    );
                  })}

                  {designControlFields.map((field) => {
                    const value = registeredFieldValue(form, field, field.defaultValue ?? "");
                    const error = registryFieldError(field, value);
                    if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
                      return (
                        <FormControl key={field.path} fullWidth size="small" error={Boolean(error)}>
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            label={field.label}
                            value={value}
                            disabled={saving || moduleUnavailable}
                            onChange={(event) => {
                              if (!registryFieldError(field, event.target.value)) onCommit(field.path, event.target.value);
                            }}
                          >
                            {(field.options || []).map((option) => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </Select>
                          {error ? <FormHelperText>{error}</FormHelperText> : null}
                        </FormControl>
                      );
                    }
                    if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
                      if (field.control === "slider" && Number.isFinite(Number(field.limits?.min)) && Number.isFinite(Number(field.limits?.max))) {
                        const unitMatch = String(field.label || "").match(/\(([^)]+)\)\s*$/);
                        return (
                          <VisualSliderControl
                            key={field.path}
                            label={field.label}
                            value={value}
                            min={field.limits.min}
                            max={field.limits.max}
                            step={field.limits.step ?? 1}
                            unit={unitMatch?.[1] || "px"}
                            disabled={saving || moduleUnavailable}
                            helperText={field.helperText || "El cambio se refleja inmediatamente en la vista previa."}
                            nullable={field.nullable === true}
                            fallbackValue={field.inheritedValuePath ? getAtPath(form, field.inheritedValuePath, field.inheritedFallback) : field.inheritedFallback}
                            resetLabel={field.resetLabel || "Heredar"}
                            onChange={(nextValue) => {
                              if (!registryFieldError(field, nextValue)) onCommit(field.path, nextValue);
                            }}
                            onReset={() => onCommit(field.path, "")}
                          />
                        );
                      }
                      return (
                        <TextField
                          key={`${field.path}:${String(value)}`}
                          fullWidth
                          size="small"
                          type="number"
                          label={field.label}
                          defaultValue={value}
                          disabled={saving || moduleUnavailable}
                          error={Boolean(error)}
                          inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }}
                          helperText={error || "El cambio se refleja inmediatamente en la vista previa."}
                          onChange={(event) => {
                            const raw = event.target.value;
                            const nextValue = raw === "" ? "" : Number(raw);
                            if (!registryFieldError(field, nextValue)) onCommit(field.path, nextValue);
                          }}
                        />
                      );
                    }
                    return null;
                  })}
                </Stack>
              </Box>

              <InspectorAccordion
                id="colors"
                expanded={expandedDesignSection}
                onChange={setExpandedDesignSection}
                icon={PaletteRoundedIcon}
                title="Colores"
                description="Elegí entre la paleta global o una paleta exclusiva para esta sección"
              >
                <Stack spacing={1.5}>
                  {canUseSectionPalette ? (
                    <>
                      <Box sx={{ p: 1.25, bgcolor: "#F7F8FA", borderRadius: 3 }}>
                        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                              Usar colores globales
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25, color: "rgba(25,36,47,.62)" }}>
                              {useGlobalSectionColors
                                ? "Esta sección hereda la paleta global. Si cambia el tema, se actualiza automáticamente."
                                : "Esta sección usa una paleta propia y no modifica ni hereda cambios de otras secciones."}
                            </Typography>
                          </Box>
                          <Switch
                            checked={useGlobalSectionColors}
                            disabled={saving}
                            onChange={(event) => commitSectionColorMode(event.target.checked)}
                            inputProps={{ "aria-label": "Usar colores globales en esta sección" }}
                          />
                        </Stack>
                      </Box>

                      {!useGlobalSectionColors ? (
                        <Box sx={{ p: 1.25, bgcolor: "#fff", borderRadius: 3, border: "1px solid rgba(25,36,47,.09)" }}>
                          <Typography sx={{ fontWeight: 950, color: "#19242F" }}>
                            Colores de esta sección
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.3, mb: 1.15, color: "rgba(25,36,47,.62)" }}>
                            Empezamos con los globales actuales, pero desde acá cualquier cambio queda guardado sólo en este bloque.
                          </Typography>
                          <Stack spacing={1}>
                            {SECTION_COLOR_TOKENS.map(([key, paletteLabel]) => {
                              const rawValue = normalizeEditableColor(sectionPaletteDraft[key]);
                              const pickerValue = toColorInput(rawValue);
                              const transparent = isTransparentColor(rawValue);
                              const displayedValue = rawValue || pickerValue;
                              return (
                                <Stack key={key} direction="row" spacing={1} alignItems="center" sx={{ p: 0.85, bgcolor: "#F7F8FA", borderRadius: 2.5 }}>
                                  <Box
                                    component="input"
                                    type="color"
                                    aria-label={paletteLabel}
                                    value={pickerValue}
                                    disabled={saving}
                                    onChange={(event) => setSectionPaletteDraft((previous) => ({ ...previous, [key]: event.target.value }))}
                                    sx={{
                                      width: 42,
                                      height: 38,
                                      p: 0.2,
                                      border: "1px solid rgba(25,36,47,.15)",
                                      borderRadius: 2,
                                      bgcolor: transparent ? "transparent" : "#fff",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                      opacity: transparent ? 0.45 : 1,
                                    }}
                                  />
                                  <TextField
                                    size="small"
                                    fullWidth
                                    label={paletteLabel}
                                    value={rawValue}
                                    disabled={saving}
                                    placeholder="transparent o #RRGGBB"
                                    onChange={(event) => setSectionPaletteDraft((previous) => ({ ...previous, [key]: event.target.value }))}
                                    helperText={transparent ? "Valor actual: transparent" : `Valor actual: ${displayedValue}`}
                                    onFocus={(event) => event.target.select()}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Stack direction="row" spacing={0.25} alignItems="center">
                                            <Button
                                              size="small"
                                              disabled={saving}
                                              onClick={() => setSectionPaletteDraft((previous) => ({ ...previous, [key]: "transparent" }))}
                                              sx={{ minWidth: 0, px: 0.65, textTransform: "none", fontSize: ".68rem", fontWeight: 900 }}
                                            >
                                              Transparente
                                            </Button>
                                            <SectionColorCopyButton
                                              label={paletteLabel}
                                              value={displayedValue}
                                              disabled={saving}
                                            />
                                          </Stack>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Stack>
                              );
                            })}
                          </Stack>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={applySectionPalette}
                            disabled={saving}
                            sx={{ mt: 1.15, borderRadius: 3, bgcolor: "#F35A3A", textTransform: "none", fontWeight: 950 }}
                          >
                            Aplicar sólo en esta sección
                          </Button>
                        </Box>
                      ) : null}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      Los colores globales se editan desde la configuración general del sitio.
                    </Alert>
                  )}
                </Stack>
              </InspectorAccordion>

              <InspectorAccordion
                id="type"
                expanded={expandedDesignSection}
                onChange={setExpandedDesignSection}
                icon={FormatSizeRoundedIcon}
                title="Tipografía"
                description="Tamaño, fuerza visual y familia de letra"
              >
                <Stack spacing={1.7}>
                  {visualField ? (
                    <>
                      <LevelSelector
                        label="Tamaño de los títulos de este segmento"
                        value={visual.titleSize}
                        options={titleSizeOptions}
                        onChange={(titleSize) => updateVisual({ titleSize })}
                      />
                      <LevelSelector
                        label="Fuerza visual de los títulos"
                        value={visual.textStyle}
                        options={textStyleOptions}
                        onChange={(textStyle) => updateVisual({ textStyle })}
                      />
                      <Divider />
                    </>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      Este segmento no declara ajustes tipográficos propios en el registro.
                    </Alert>
                  )}

                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    Familia tipográfica de toda la página
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                    {FONT_PRESETS.map((preset) => {
                      const selected = form?.theme?.headingFontFamily === preset.heading && form?.theme?.bodyFontFamily === preset.body;
                      return (
                        <Button
                          key={preset.id}
                          variant={selected ? "contained" : "outlined"}
                          onClick={() => onCommitMany({
                            "theme.fontFamily": preset.body,
                            "theme.headingFontFamily": preset.heading,
                            "theme.bodyFontFamily": preset.body,
                            "theme.googleFontsUrl": preset.url,
                          })}
                          sx={{ minHeight: 74, borderRadius: 3, textTransform: "none", fontWeight: 900, bgcolor: selected ? "#19242F" : "#fff" }}
                        >
                          <Stack>
                            <Typography sx={{ fontFamily: preset.heading, fontWeight: 900 }}>{preset.label}</Typography>
                            <Typography variant="caption" sx={{ opacity: .72 }}>{preset.heading} + {preset.body}</Typography>
                          </Stack>
                        </Button>
                      );
                    })}
                  </Box>
                  <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>
                    La familia de letra se mantiene global para conservar coherencia entre secciones.
                  </Typography>
                </Stack>
              </InspectorAccordion>

              {imageFields.length ? (
                <InspectorAccordion
                  id="image-design"
                  expanded={expandedDesignSection}
                  onChange={setExpandedDesignSection}
                  icon={ImageRoundedIcon}
                  title="Diseño de imágenes"
                  description="Tamaño visual y encuadre, sin mezclar la selección del archivo"
                >
                  <Stack spacing={1.6}>
                    {visualField ? (
                      <LevelSelector
                        label="Tamaño visual de las imágenes de este segmento"
                        value={visual.imageSize}
                        options={imageSizeOptions}
                        onChange={(imageSize) => updateVisual({ imageSize })}
                      />
                    ) : null}

                    {imageFields.filter((field) => field.positionPath && field.positionOptions?.length).map((field) => {
                      const value = getAtPath(
                        form,
                        field.positionPath,
                        field.positionDefaultValue || field.positionOptions[0]?.value || "center"
                      );
                      const positionDefinition = {
                        type: SEGMENT_FIELD_TYPES.SELECT,
                        options: field.positionOptions,
                      };
                      const error = registryFieldError(positionDefinition, value);
                      return (
                        <FormControl key={field.positionPath} fullWidth size="small" error={Boolean(error)}>
                          <InputLabel>{field.positionLabel || `Posición de ${field.label}`}</InputLabel>
                          <Select
                            label={field.positionLabel || `Posición de ${field.label}`}
                            value={value}
                            disabled={saving || moduleUnavailable}
                            onChange={(event) => {
                              if (!registryFieldError(positionDefinition, event.target.value)) {
                                onCommit(field.positionPath, event.target.value);
                              }
                            }}
                          >
                            {field.positionOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {error ? <FormHelperText>{error}</FormHelperText> : null}
                        </FormControl>
                      );
                    })}
                  </Stack>
                </InspectorAccordion>
              ) : null}

              <InspectorAccordion
                id="advanced"
                expanded={expandedDesignSection}
                onChange={setExpandedDesignSection}
                icon={TuneRoundedIcon}
                title="Opciones avanzadas"
                description="Ajustes visuales que normalmente no necesitás cambiar"
              >
                <Stack spacing={1.8}>
                  {overlayField ? (
                    <LevelSelector
                      label="Oscuridad sobre la imagen"
                      value={String(getAtPath(form, overlayField.path, "")).includes(".82") ? 3 : String(getAtPath(form, overlayField.path, "")).includes(".48") ? 1 : 2}
                      options={[{ value: 1, label: "Suave" }, { value: 2, label: "Legible" }, { value: 3, label: "Intensa" }]}
                      onChange={(value) => onCommit(
                        overlayField.path,
                        value === 1
                          ? "linear-gradient(to bottom, rgba(0,0,0,.08), rgba(8,8,20,.48))"
                          : value === 3
                            ? "linear-gradient(to bottom, rgba(0,0,0,.28), rgba(8,8,20,.82))"
                            : "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))"
                      )}
                    />
                  ) : null}

                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 900 }}>
                      Redondez general de tarjetas y botones
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={Number(form?.theme?.borderRadius || 10) <= 5 ? 1 : Number(form?.theme?.borderRadius || 10) >= 20 ? 3 : 2}
                      aria-label="Redondez general de tarjetas y botones"
                      onChange={(_, next) => next && onCommit("theme.borderRadius", next === 1 ? 4 : next === 3 ? 24 : 10)}
                    >
                      <ToggleButton value={1}>Recto</ToggleButton>
                      <ToggleButton value={2}>Suave</ToggleButton>
                      <ToggleButton value={3}>Redondo</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {!overlayField ? (
                    <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>
                      Esta sección no necesita ajustes avanzados adicionales.
                    </Typography>
                  ) : null}
                </Stack>
              </InspectorAccordion>
            </Stack>
          ) : null}

          {INDEX_TAB[tab] === "behavior" ? (
            <Stack spacing={1.5}>
              {moduleUnavailable ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Este segmento depende del módulo de productos o servicios. Activá ese módulo antes de mostrarlo.
                </Alert>
              ) : null}

              {visibilityPath ? (
                <Box
                  sx={{
                    p: 1.45,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    border: "1px solid rgba(25,36,47,.09)",
                  }}
                >
                  <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                    Visibilidad
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, mb: 1.1, color: "rgba(25,36,47,.62)" }}>
                    Decidí si esta sección forma parte de la página sin alterar su contenido ni su diseño.
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={visible}
                        disabled={saving || moduleUnavailable}
                        onChange={(event) => setVisible(event.target.checked)}
                      />
                    )}
                    label={visible ? "Esta sección está visible" : "Esta sección está oculta"}
                    title={visibilityField?.label || "Visibilidad del segmento"}
                    sx={{ m: 0, width: "100%" }}
                  />
                </Box>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Esta sección no tiene un control de visibilidad independiente.
                </Alert>
              )}

              {behaviorFields.length ? (
                <Box
                  sx={{
                    p: 1.45,
                    borderRadius: 3,
                    bgcolor: "#fff",
                    border: "1px solid rgba(25,36,47,.09)",
                  }}
                >
                  <Typography sx={{ color: "#19242F", fontWeight: 950 }}>
                    Enlaces y comportamiento
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, mb: 1.25, color: "rgba(25,36,47,.62)" }}>
                    Configurá destinos y reglas de funcionamiento sin mezclar estos valores con los textos visibles.
                  </Typography>
                  <Stack spacing={1.2}>
                    {behaviorFields.map((field) => {
                      const value = registeredFieldValue(form, field, field.defaultValue ?? "");
                      if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
                        return (
                          <FormControlLabel
                            key={field.path}
                            control={(
                              <Switch
                                checked={value === true}
                                disabled={saving || moduleUnavailable}
                                onChange={(event) => onCommit(field.path, event.target.checked)}
                              />
                            )}
                            label={field.label}
                            sx={{ m: 0 }}
                          />
                        );
                      }

                      if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
                        const error = registryFieldError(field, value);
                        return (
                          <FormControl key={field.path} fullWidth size="small" error={Boolean(error)}>
                            <InputLabel>{field.label}</InputLabel>
                            <Select
                              label={field.label}
                              value={value}
                              disabled={saving || moduleUnavailable}
                              onChange={(event) => {
                                if (!registryFieldError(field, event.target.value)) onCommit(field.path, event.target.value);
                              }}
                            >
                              {(field.options || []).map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </Select>
                            {error ? <FormHelperText>{error}</FormHelperText> : null}
                          </FormControl>
                        );
                      }

                      if (field.type === SEGMENT_FIELD_TYPES.URL && field.destinationPicker === true) {
                        return (
                          <Box key={`${field.path}:${String(value)}`}>
                            <Typography variant="body2" sx={{ mb: 0.7, fontWeight: 900, color: "#19242F" }}>
                              {field.label}
                            </Typography>
                            <ButtonDestinationEditor
                              field={field}
                              value={value}
                              options={internalAnchorOptions}
                              disabled={saving || moduleUnavailable}
                              onCommit={onCommit}
                            />
                          </Box>
                        );
                      }

                      return (
                        <RegisteredBehaviorTextField
                          key={field.path}
                          field={field}
                          value={value}
                          form={form}
                          baselineForm={baselineForm}
                          disabled={saving || moduleUnavailable}
                          onCommit={onCommit}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}

              <Typography variant="caption" sx={{ px: 0.5, color: "rgba(25,36,47,.58)" }}>
                Abrir, cerrar o cambiar de pestaña no modifica valores. Los cambios se aplican solamente cuando usás un control.
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
